import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import { getAccounts, inviteUserToAccount } from "./api";

/**
 * SCK-741 — SYSTEM-user invite surface on Directory → Users.
 *
 * SYSTEM users land on <SystemUsersTable/> (cross-tenant read-only
 * roster); they have no account context, so the existing
 * MemberFormDialog / DirectoryAccountDetail.InviteUserDialog can't
 * run without first picking a target account.
 *
 * This dialog is the SYSTEM-scoped equivalent: an Autocomplete over
 * every account the caller can see, followed by the same email /
 * role / canCreateProjects fields as the per-account dialog. On
 * submit it hits POST /api/accounts/{selected}/members and returns
 * the SCK-617 `{invitation, member}` response via onSaved so the
 * caller can fire CopyLinkModal with the branded Email tab (SCK-738).
 *
 * Props:
 *   open                        boolean
 *   onClose()                   close + reset
 *   onSaved({ email, response }) called with the raw mint response
 *                                (SCK-617 shape) + the entered email
 *                                so the parent can route it through
 *                                handleInviteResponse → CopyLinkModal.
 */
export default function SystemInviteUserDialog({ open, onClose, onSaved }) {
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsErr, setAccountsErr] = useState(null);

  const [account, setAccount] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("MEMBER");
  const [canCreate, setCanCreate] = useState(false);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) {
      setAccount(null);
      setEmail("");
      setRole("MEMBER");
      setCanCreate(false);
      setErr(null);
      return;
    }
    let cancelled = false;
    setAccountsLoading(true);
    setAccountsErr(null);
    getAccounts()
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.content)
            ? data.content
            : [];
        setAccounts(list);
      })
      .catch((e) => {
        if (!cancelled) setAccountsErr(e);
      })
      .finally(() => {
        if (!cancelled) setAccountsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    // Admin role implies create-projects. Keep in sync with
    // DirectoryAccountDetail.InviteUserDialog so the two surfaces
    // behave the same way.
    if (role === "ACCOUNT_ADMIN") setCanCreate(true);
  }, [role]);

  const canSubmit = !!account && !!email && !busy;

  const accountOptions = useMemo(
    () =>
      accounts
        .slice()
        .sort((a, b) =>
          (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }),
        ),
    [accounts],
  );

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      const response = await inviteUserToAccount(account.id, {
        email,
        role,
        canCreateProjects: canCreate,
      });
      onSaved && onSaved({ email, response });
    } catch (e) {
      if (e.status === 409) {
        setErr("That email is already a member of this account.");
      } else {
        setErr(`Failed to invite: HTTP ${e.status || "?"} — ${e.message || e}`);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={!!open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="system-invite-user-title"
    >
      <DialogTitle id="system-invite-user-title">Invite a user</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            As a SYSTEM operator you can invite into any account you can see.
            Pick the target account first, then the invitee's email and the
            account-level role.
          </Typography>

          {accountsErr && (
            <Alert severity="error">
              Couldn't load accounts: {accountsErr.message || "unknown error"}
            </Alert>
          )}

          <Autocomplete
            options={accountOptions}
            value={account}
            onChange={(_, v) => setAccount(v)}
            loading={accountsLoading}
            getOptionLabel={(opt) => (opt ? `${opt.name || "(unnamed)"}` : "")}
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
            renderOption={(props, opt) => (
              <li {...props} key={opt.id}>
                <span style={{ display: "flex", flexDirection: "column" }}>
                  <span>{opt.name || "(unnamed)"}</span>
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      color: "rgba(0,0,0,0.54)",
                    }}
                  >
                    {opt.id}
                  </span>
                </span>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Target account"
                required
                placeholder={accountsLoading ? "Loading accounts…" : "Pick an account"}
              />
            )}
            fullWidth
            data-testid="system-invite-account-picker"
          />

          <TextField
            label="Email"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            inputProps={{ "data-testid": "system-invite-email-input" }}
          />

          <FormControl fullWidth>
            <InputLabel>Account role</InputLabel>
            <Select
              label="Account role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="system-invite-role-select"
            >
              <MenuItem value="MEMBER">Member</MenuItem>
              <MenuItem value="ACCOUNT_ADMIN">Account Admin</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={canCreate}
                onChange={(e) => setCanCreate(e.target.checked)}
                disabled={role === "ACCOUNT_ADMIN"}
              />
            }
            label={
              role === "ACCOUNT_ADMIN"
                ? "Can create workspaces (admins always can)"
                : "Can create new workspaces"
            }
          />

          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!canSubmit}
          data-testid="system-invite-submit"
        >
          {busy ? "Inviting…" : "Invite"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
